import { format } from 'date-fns';
import Router from 'next/router';
import { memo, useCallback, useEffect, useRef } from 'react';

import {
  type OpenCreditPackModalLocations,
  useTrackCreditPackCancellationInitiated,
  useTrackCreditPackCancelled,
  useTrackCreditPackDowngrade,
  useTrackCreditPackDowngradeInitiated,
} from '@air/analytics';
import { Button } from '@air/primitive-button';
import { ModalDescription } from '@air/primitive-modal';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { CreditPackLineItem } from '~/components/Modals/ManageCreditPacks/CreditPackLineItem';
import { CreditPackModalWrapper } from '~/components/Modals/ManageCreditPacks/CreditPackModalWrapper';
import { UpdateCreditPacksModal } from '~/components/Modals/ManageCreditPacks/UpdateCreditPacksModal';
import { isSubscriptionCheckoutRoute, Routes } from '~/constants/routes';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useSubscriptionPlan } from '~/swr-hooks/subscriptions/useSubscriptionPlan';
import { useUpdateCreditPacks } from '~/swr-hooks/subscriptions/useUpdateCreditPacks';
import { formatCreditCount, formatMonthlyCredits } from '~/utils/creditUtils';
import { getSubscriptionInfo } from '~/utils/SubscriptionUtils';

export type ManageCreditPacksConfirmationModalProps = AirModalProps<{
  targetQuantity: number;
  /** Surface that opened the manage credit packs flow. Pass `undefined` explicitly when there is
   *  no attributable surface. */
  source: OpenCreditPackModalLocations | undefined;
  /** When true, clicking Cancel re-opens `UpdateCreditPacksModal` with the user's selection
   *  preserved. Used when this modal is launched from the picker itself; other callers should
   *  leave it off so Cancel just closes. */
  showUpdateCreditPacksModalOnCancel?: boolean;
  /** Forwarded to the re-opened `UpdateCreditPacksModal` on cancel so the picker's upgrade
   *  branch keeps the original callback (e.g. checkout's shallow URL replace) instead of
   *  falling back to `Router.push`. */
  onUpgradeConfirmed?: (quantity: number) => void;
}>;

export const ManageCreditPacksConfirmationModal = memo(
  ({
    targetQuantity,
    source,
    showUpdateCreditPacksModalOnCancel,
    onUpgradeConfirmed,
    onClose,
  }: ManageCreditPacksConfirmationModalProps) => {
    const { data: subscription } = useSubscriptionContext();
    const { data: subscriptionPlan } = useSubscriptionPlan();
    const { currentWorkspace } = useCurrentWorkspace();
    const { showToast } = useToasts();
    const { mutateAsync: updateCreditPacks, isPending: isUpdatingCreditPacks } = useUpdateCreditPacks();
    const { trackCreditPackDowngradeInitiated } = useTrackCreditPackDowngradeInitiated();
    const { trackCreditPackDowngrade } = useTrackCreditPackDowngrade();
    const { trackCreditPackCancellationInitiated } = useTrackCreditPackCancellationInitiated();
    const { trackCreditPackCancelled } = useTrackCreditPackCancelled();
    const creditAnalyticsParams = useCreditAnalyticsParams();
    const [showUpdateCreditPacksModal] = useAirModal(UpdateCreditPacksModal);
    const hasTrackedInitiatedRef = useRef<boolean>(false);

    useEffect(() => {
      if (!creditAnalyticsParams || hasTrackedInitiatedRef.current) {
        return;
      }

      hasTrackedInitiatedRef.current = true;
      if (targetQuantity > 0) {
        trackCreditPackDowngradeInitiated({
          ...creditAnalyticsParams,
          pack_quantity: targetQuantity,
          source,
        });
      } else {
        // `'checkout'` source is structurally unreachable here — the form picker has no zero
        // option, so checkout's downgrade flow can never land on the cancellation branch.
        // Narrowed for type alignment.
        trackCreditPackCancellationInitiated({
          ...creditAnalyticsParams,
          source: source === 'checkout' ? undefined : source,
        });
      }
    }, [
      creditAnalyticsParams,
      source,
      targetQuantity,
      trackCreditPackCancellationInitiated,
      trackCreditPackDowngradeInitiated,
    ]);

    const handleCancel = useCallback(() => {
      if (showUpdateCreditPacksModalOnCancel) {
        showUpdateCreditPacksModal({ initialSelectedQuantity: targetQuantity, source, onUpgradeConfirmed });
      }

      onClose();
    }, [
      onClose,
      onUpgradeConfirmed,
      showUpdateCreditPacksModal,
      showUpdateCreditPacksModalOnCancel,
      source,
      targetQuantity,
    ]);

    const handleConfirm = useCallback(async () => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId || !subscription) {
        return;
      }

      try {
        await updateCreditPacks({ workspaceId, quantity: targetQuantity });

        if (creditAnalyticsParams) {
          if (targetQuantity > 0) {
            trackCreditPackDowngrade({
              ...creditAnalyticsParams,
              pack_quantity: targetQuantity,
              source,
            });
          } else {
            // `'checkout'` source is structurally unreachable here — see comment above the
            // cancellation_initiated firing site.
            trackCreditPackCancelled({
              ...creditAnalyticsParams,
              source: source === 'checkout' ? undefined : source,
            });
          }
        }

        if (isSubscriptionCheckoutRoute(Router.pathname)) {
          await Router.replace(Routes.workspaceAdmin.subscription);
        }

        onClose();
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to reduce credit packs via confirmation modal',
          metadata: { data: { workspaceId, quantity: targetQuantity, plan: subscription.plan } },
        });
        showToast('Failed to update credit packs. Please try again.');
      }
    }, [
      creditAnalyticsParams,
      currentWorkspace?.id,
      onClose,
      showToast,
      source,
      subscription,
      targetQuantity,
      trackCreditPackCancelled,
      trackCreditPackDowngrade,
      updateCreditPacks,
    ]);

    const title = targetQuantity > 0 ? 'Downgrade credit pack' : 'Remove credit pack';

    const subscriptionInfo = getSubscriptionInfo({
      subscription,
      plan: subscriptionPlan?.plan,
      totalBillableSeats: 0,
    });

    if (!subscription || !subscriptionInfo?.creditPack || !subscriptionInfo.billingCycle) {
      return (
        <CreditPackModalWrapper
          title={title}
          isLoading
          onClose={onClose}
          testId="MANAGE_CREDIT_PACKS_CONFIRMATION_MODAL"
        />
      );
    }

    const {
      billingCycle,
      planPriceCents,
      planCredits,
      creditPack,
      totalCredits: currentTotalCredits,
    } = subscriptionInfo;

    const newPackCredits = targetQuantity * creditPack.size;
    const newPackPriceCents = targetQuantity * creditPack.pricePerPackCents;
    const newTotalPriceCents = planPriceCents + newPackPriceCents;

    return (
      <CreditPackModalWrapper title={title} onClose={onClose} testId="MANAGE_CREDIT_PACKS_CONFIRMATION_MODAL">
        <ModalDescription className="ads:text-metadata text-14 text-gray-10">
          {targetQuantity > 0 ? (
            <>
              You can adjust your monthly credit limit at any time. Your current limit of{' '}
              {formatCreditCount(currentTotalCredits)} credits will be available until the end of your billing cycle.
              After that, your card on file will be charged the new price starting on your next billing date.
            </>
          ) : (
            <>
              Are you sure you want to remove your {formatCreditCount(creditPack.credits)} credits Credit Pack from your
              current plan? Your remaining credits will be available until the end of your billing cycle, and your
              updated monthly plan will then be in effect.
            </>
          )}
        </ModalDescription>

        <CreditPackLineItem
          label={`${subscription.planName} Plan`}
          priceInCents={planPriceCents}
          billingCycle={billingCycle}
          description={formatMonthlyCredits(planCredits)}
        />

        {targetQuantity > 0 ? (
          <CreditPackLineItem
            label="New credit pack"
            priceInCents={newPackPriceCents}
            billingCycle={billingCycle}
            description={formatMonthlyCredits(newPackCredits)}
          />
        ) : (
          <CreditPackLineItem
            label="Credit pack"
            priceInCents={creditPack.totalPriceCents}
            billingCycle={billingCycle}
            description={formatMonthlyCredits(creditPack.credits)}
            strikethrough
          />
        )}

        <CreditPackLineItem
          label="Updated price"
          priceInCents={newTotalPriceCents}
          billingCycle={billingCycle}
          description={
            <>
              {formatMonthlyCredits(planCredits + newPackCredits)}
              {subscription?.nextBillingDate &&
                ` · Next billing date ${format(new Date(subscription.nextBillingDate), 'MMMM d, yyyy')}`}
            </>
          }
          separator
        />

        <footer className="flex justify-between gap-2">
          <Button
            appearance="outline"
            color="gray"
            onClick={handleCancel}
            size="large"
            data-testid="MANAGE_CREDIT_PACKS_KEEP_BUTTON"
          >
            Keep current pack
          </Button>
          <Button
            appearance="filled"
            color={targetQuantity > 0 ? 'blue' : 'red'}
            disabled={isUpdatingCreditPacks}
            isLoading={isUpdatingCreditPacks}
            onClick={handleConfirm}
            size="large"
            data-testid="MANAGE_CREDIT_PACKS_CONFIRMATION_BUTTON"
          >
            {targetQuantity > 0 ? 'Downgrade' : 'Remove'} credit pack
          </Button>
        </footer>
      </CreditPackModalWrapper>
    );
  },
);

ManageCreditPacksConfirmationModal.displayName = 'ManageCreditPacksConfirmationModal';
